<script setup>
  import { ref, inject, onMounted, computed, reactive, nextTick, watch, getCurrentInstance } from 'vue'
  import { useRouter, useRoute } from 'vue-router'
  import { saveAs } from 'file-saver'
  import Header from '@/components/Header.vue'
  import Footer from '@/components/Footer.vue'
  import xml2js from 'xml2js'
  import Loading from '@/components/Loading.vue'
  import JSZip from 'jszip'
  import { useDialogStore } from '@/stores/dialogStore'
  import { useEgovApplicationStore } from '@/stores/egovApplicationStore'
  import { validateCorporateNumber, convertToHalfWidthNumber, convertToFullWidth } from '@/components/logics/Util'

  import { useEgovAuthStore } from '@/stores/egovAuthStore'
  import { useRegionStore } from '@/stores/regionStore'
  import { useContractStore } from '@/stores/contractStore'

  const router = useRouter()
  const query = useRoute().query

  const dialogStore = useDialogStore()
  const egovStore = useEgovApplicationStore()
  const egovAuthStore = useEgovAuthStore()
  const regionStore = useRegionStore()
  const contractStore = useContractStore()

  const breadcrumbs = [{ name: 'ダッシュボード', url: 'dashboard' }, { name: '36協定', url: '/article36Agreement' }, { name: 'e-Gov電子申請' }]

  // リファレンス変数（UI状態管理用）
  const loading = ref(null)
  const scaleList = [0.5, 0.75, 1, 1.25, 1.5]
  const scaleIndex = ref(2)
  const showKousei = ref(true)
  const fileInput = ref(null)

  const builder = new xml2js.Builder({
    xmldec: { standalone: null, encoding: 'UTF-8' },
    renderOpts: { pretty: false },
  })

  // computed
  const scale = computed(() => scaleList[scaleIndex.value])
  const isRecreate = computed(() => query.action === 'recreate' && query.egovId)

  // watch - ストアの状態を監視してisCheckedをfalseにする
  watch(
    () => egovStore.applicantInfo,
    () => {
      egovStore.isChecked = false
    },
    { deep: true }
  )

  watch(
    () => egovStore.contactInfo,
    () => {
      egovStore.isChecked = false
    },
    { deep: true }
  )
  watch(
    () => egovStore.attachedFiles,
    () => {
      egovStore.isChecked = false
    },
    { deep: true }
  )

  watch([() => egovStore.major, () => egovStore.medium], () => {
    egovStore.isChecked = false
  })

  // handler /////////////////////////////////////////

  // 全角文字入力の処理（申請者情報用）
  const handleApplicantFullWidthInput = (event, key) => {
    egovStore.applicantInfo[key] = convertToFullWidth(event.target.value)
  }

  // 全角文字入力の処理（連絡先情報用）
  const handleContactFullWidthInput = (event, key) => {
    egovStore.contactInfo[key] = convertToFullWidth(event.target.value)
  }

  // 申請者電話番号入力時の処理
  const handleApplicantPhoneInput = (event, index) => {
    // 全角を半角に変換
    const halfWidthValue = convertToHalfWidthNumber(event.target.value)
    egovStore.applicantInfo.phone[index] = halfWidthValue
  }

  // 申請者FAX番号入力時の処理
  const handleApplicantFaxInput = (event, index) => {
    // 全角を半角に変換
    const halfWidthValue = convertToHalfWidthNumber(event.target.value)
    egovStore.applicantInfo.fax[index] = halfWidthValue
  }

  // 申請者郵便番号入力時の処理
  const handleApplicantPostalCodeInput = async (event, index) => {
    // 全角を半角に変換
    const halfWidthValue = convertToHalfWidthNumber(event.target.value)
    egovStore.applicantInfo.postalCode[index] = halfWidthValue

    // 両方の入力が揃っているか確認
    if (egovStore.isApplicantPostalCodeFilled()) {
      egovStore.setApplicantAddress(await regionStore.getAddressWithPostalCode(egovStore.applicantPostalCodeWithHyphen))
    }
  }

  // 連絡先電話番号入力時の処理
  const handleContactPhoneInput = (event, index) => {
    // 全角を半角に変換
    const halfWidthValue = convertToHalfWidthNumber(event.target.value)
    egovStore.contactInfo.phone[index] = halfWidthValue
  }

  // 連絡先FAX番号入力時の処理
  const handleContactFaxInput = (event, index) => {
    // 全角を半角に変換
    const halfWidthValue = convertToHalfWidthNumber(event.target.value)
    egovStore.contactInfo.fax[index] = halfWidthValue
  }

  // 連絡先郵便番号入力時の処理
  const handleContactPostalCodeInput = async (event, index) => {
    // 全角を半角に変換
    const halfWidthValue = convertToHalfWidthNumber(event.target.value)
    egovStore.contactInfo.postalCode[index] = halfWidthValue

    // 両方の入力が揃っているか確認
    if (egovStore.isContactPostalCodeFilled()) {
      egovStore.setContactAddress(await regionStore.getAddressWithPostalCode(egovStore.contactPostalCodeWithHyphen))
    }
  }

  // 申請者メールアドレス入力時：使えない文字を即座に除去
  const handleApplicantEmailInput = event => {
    // メールで使える文字以外を除去（a-zA-Z0-9._%+-@のみ許可）
    const filtered = event.target.value.replace(/[^a-zA-Z0-9._%+\-@]/g, '')
    egovStore.applicantInfo.email = filtered
    event.target.value = filtered
  }

  // 連絡先メールアドレス入力時：使えない文字を即座に除去
  const handleContactEmailInput = event => {
    const filtered = event.target.value.replace(/[^a-zA-Z0-9._%+\-@]/g, '')
    egovStore.contactInfo.email = filtered
    event.target.value = filtered
  }

  // methods /////////////////////////////////////////

  onMounted(async () => {
    if (!(await contractStore.getIsActive())) {
      await dialogStore.showMessageDialog('エラー', '有料プランの契約が必要です')
      router.back()
      return
    }

    // ローディング開始
    loading.value.show()

    try {
      // 前の状態をクリア
      egovStore.resetFormState()

      // データ取得
      await regionStore.getPrefectures()

      // 認証が必要な場合は認証開始
      if (await egovAuthStore.checkAuthRequired()) {
        // ダイアログ表示前にローディング停止
        loading.value.hide()

        if (await dialogStore.showConfirmDialog('認証', 'e-Gov電子申請を利用するには認証が必要です。認証を開始しますか？')) {
          await egovAuthStore.startAuth()
          return // 認証画面に遷移するのでここで終了（ローディングは既に非表示）
        } else {
          router.back()
          return
        }
      }

      // 認証済みの場合は通常処理を続行（ローディング継続中）
      // 必須データ取得の配列
      const requiredDataTasks = [
        { task: () => egovStore.getEgovMetadata(), error: 'e-Govデータの取得に失敗しました。前の画面に戻ります' },
        { task: () => egovStore.loadPublicOrgInfo(), error: '労働基準監督署の情報の取得に失敗しました。前の画面に戻ります' },
        { task: () => egovStore.loadKouseiInfo(), error: '36協定の情報の取得に失敗しました。前の画面に戻ります' },
        { task: () => egovStore.loadProcedureInfo(query.procId), error: '手続き情報の取得に失敗しました。前の画面に戻ります' },
      ]

      // 必須データを順次処理
      for (const { task, error } of requiredDataTasks) {
        if (!(await task())) {
          // エラー時はローディング停止してからダイアログ表示
          loading.value.hide()
          await dialogStore.showAlertDialog('エラー', error)
          router.back()
          return
        }
      }

      if (isRecreate.value) {
        // 再作成時の処理
        if (!(await egovStore.loadApplicationForResubmission(query.egovId))) {
          loading.value.hide()
        }
      } else {
        await egovStore.initShinseisha(query.branchId)
        await egovStore.initRenrakusaki()
        await egovStore.getSubmissionInfo(query.branchId, query.agreementId)
      }

      // フォーム初期化
      const initialFormId = egovStore.currentFormID || egovStore.firstFormId
      if (initialFormId) {
        await createForm(initialFormId)
      } else {
      }
    } catch (error) {
      loading.value.hide()
      await dialogStore.showAlertDialog('エラー', 'データの取得に失敗しました。前の画面に戻ります。')
      router.back()
      return
    } finally {
      // 最終的に確実にローディング停止
      loading.value.hide()
    }
  })
  // ファイル選択
  const selectFile = async (item, event) => {
    const file = event.target.files[0]
    const data = await new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result)
      reader.onerror = error => reject(error)
      reader.readAsText(file)
    })

    const index = egovStore.attachedFiles.findIndex(f => f === item)
    egovStore.setAttachedFile(index, file.name, data)
    event.target.value = ''
  }

  // ファイルクリア
  const clearFile = async item => {
    if (await dialogStore.showConfirmDialog('ディーモ', '添付ファイルを削除しますか？')) {
      const index = egovStore.attachedFiles.findIndex(f => f === item)
      egovStore.clearAttachedFile(index)
    }
  }

  // プレビュー切り替え
  const changePreview = () => {
    egovStore.togglePreview()
    createForm(egovStore.currentFormID)
  }

  // ズームイン
  const zoomIn = () => {
    if (scaleIndex.value < scaleList.length - 1) {
      scaleIndex.value++
    }
  }

  // ズームアウト
  const zoomOut = () => {
    if (scaleIndex.value > 0) {
      scaleIndex.value--
    }
  }

  // 保存データダウンロード
  const downloadSaveData = async () => {
    const data = egovStore.generateSaveData()
    if (!data) return

    const a = document.createElement('a')
    a.href = URL.createObjectURL(new Blob([data], { type: 'application/json' }))
    a.download = `${egovStore.getDateTimeString()}_${egovStore.adminProcName}.json`
    a.click()
  }

  // 保存データ選択
  const selectSaveData = async event => {
    const file = event.target.files[0]
    const data = await new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result)
      reader.onerror = error => reject(error)
      reader.readAsText(file)
    })

    const result = await egovStore.loadSaveData(data)
    if (!result.success) {
      await dialogStore.showAlertDialog('エラー', result.error)
      return
    }

    createForm(egovStore.currentFormID)
    event.target.value = ''
  }

  // 内容チェックと申請ボタン
  const onApply = async () => {
    // バリデーション
    const errors = egovStore.validateApplicationData()
    if (errors.length > 0) {
      await dialogStore.showAlertDialog('エラー', errors.join('<br>'))
      return
    }

    loading.value.show()

    try {
      // 認証チェックを行う
      if (await egovAuthStore.checkAuthRequired()) {
        loading.value.hide()
        // 認証が必要な場合は認証開始
        if (await dialogStore.showConfirmDialog('認証', 'e-Gov電子申請を利用するには認証が必要です。認証を開始しますか？<br>編集中のデータがある場合、途中経過を保存してください')) {
          await egovAuthStore.startAuth()
          return // 認証画面に遷移するのでここで終了
        }
      }

      if (egovStore.isChecked) {
        // 申請実行
        try {
          const message = await egovStore.submitApplication()
          if (message) {
            // 重要: ダイアログ表示前にローディングを非表示
            loading.value.hide()

            // サーバーからのメッセージをそのまま表示（既に到達番号等の情報が含まれている）
            await dialogStore.showMessageDialog('申請完了', message)

            // ダイアログ閉じた後にダウンロード処理
            await downloadApplyData()

            // ダウンロード完了後に画面遷移
            router.push({ name: 'egovlist' })
          }
        } catch (error) {
          // 先にローディングを消してからエラーダイアログを表示
          loading.value.hide()

          // エラーメッセージを取得
          const errorMessage = error.response?.data?.message || '申請処理中にエラーが発生しました。'
          await dialogStore.showAlertDialog('エラー', errorMessage)
          return
        }
      } else {
        // 入力チェック - エラーダイアログを無効にして自分でハンドリング
        try {
          const message = await egovStore.checkApplication()
          loading.value.hide()
          if (message) {
            await dialogStore.showMessageDialog('成功', message)
          }
        } catch (error) {
          // 先にローディングを消してからエラーダイアログを表示
          loading.value.hide()

          // エラーメッセージを取得
          const errorMessage = error.response?.data?.message || 'チェック処理中にエラーが発生しました。'
          await dialogStore.showAlertDialog('エラー', errorMessage)
          return // エラー時はここで終了
        }
      }
    } catch (error) {
      // その他のエラーハンドリング
      loading.value.hide()
      await dialogStore.showAlertDialog('エラー', '予期しないエラーが発生しました。')
    } finally {
      // 念のためfinally内でもローディング表示が残っていれば消す
      if (loading.value.isShow) {
        loading.value.hide()
      }
    }
  }

  // フォーム作成（XSL変換処理）
  const createForm = async FormId => {
    // フォームIDの有効性チェック
    if (!FormId || !egovStore.formInfo.find(item => item.FormId[0] === FormId)) {
      return
    }

    loading.value.show()
    // ストアの現在フォームIDを確実に設定
    if (!egovStore.setCurrentForm(FormId)) {
      loading.value.hide()
      return
    }
    const CurrentFormInfo = egovStore.formInfo.find(item => item.FormId[0] === FormId)
    const path = `${egovStore.publicProcedureInfoObject.AdminProcInfo.AdminProcID[0]}/${CurrentFormInfo.FormId}`
    // const XMLFile = await getText(`${path}/${CurrentFormInfo.XMLFile[0]}`);
    const ServerCheckFile = await egovStore.getText(`${egovStore.activeCode}/${path}/${CurrentFormInfo.ServerCheckFile[0]}`)
    const XSLFile = await egovStore.getText(`${egovStore.activeCode}/${path}/${CurrentFormInfo.XSLFile[0]}`)

    const DataObject = egovStore.dataObjects[FormId]
    const DataTarget = DataObject['DataRoot'][Object.keys(DataObject['DataRoot']).find(key => !['様式ID', '様式バージョン', 'STYLESHEET', '様式コピー情報', 'Doctype'].includes(key))][0]
    const CheckObject = await egovStore.parseXml(ServerCheckFile)

    function validation(path, data) {
      //改行が許可されているか
      let nl = false
      const check = []
      CheckObject.checkRoot.checkItem
        .filter(item => item.errtag[0] === path)
        .map(element => {
          const list = []
          const xpath = element.xpath[0]
          const errtag = element.errtag[0]
          const inputCheck = element.inputCheck[0]
          //相関単項目に対する相関項目チェック情報
          const correlationCheckItem = element.correlationCheckItem ? element.correlationCheckItem[0] : null
          let logic = null
          let correlation = null
          if (correlationCheckItem) {
            logic = Object.keys(correlationCheckItem.logic[0])[0]
            correlation = correlationCheckItem.condition
          }

          const checkInput = (inputCheck, data, list) => {
            for (const key in inputCheck) {
              let items = []
              const object = inputCheck[key][0]
              for (const key in object) {
                let operators = []
                object[key].map(item => {
                  const keys = Object.keys(item).filter(key => key !== 'value' && key !== 'number')
                  const value = item.value || item.number || item.list || null
                  keys.forEach(key => {
                    operators.push({ key, value })
                  })
                })
                items.push({ key, operators })
              }
              let errors = []

              //省略不可（入力必須）
              if (key === 'omitDisabled') {
                if (data === '') {
                  errors.push(key)
                }
              }
              //入力不可
              if (key === 'inputDisabled') {
                if (data !== '') {
                  errors.push(key)
                }
              }

              if (key === 'my-number') {
                //12 桁の半角数字かどうかチェックする。
                if (!data.match(/[^0-9]/) || data.length !== 12) {
                  errors.push(key)
                }
              }
              if (key === 'corporate-number') {
                //13 桁の半角数字かどうかチェックする。
                if (!data.match(/[^0-9]/) || data.length !== 13) {
                  errors.push(key)
                }
              }

              if (key === 'inputData') {
                const specifiedLetters = items.find(item => item.key === 'specifiedLetter')?.operators[0].value || []
                if (specifiedLetters.includes('\\n')) {
                  nl = true
                }
                items.forEach(item => {
                  if (item.key === 'halfEnglish') {
                    if (!data.match(/[^a-zA-Z]/)) {
                      errors.push(key + ':' + item.key)
                    }
                  }
                  if (item.key === 'halfAllChar') {
                    //入力データが半角文字であること(半角カナ文字・半角空白は除く)。
                    if (!data.match(/[^a-zA-Z0-9]/) || data.match(/[\s]/)) {
                      errors.push(key + ':' + item.key)
                    }
                  }
                  if (item.key === 'fullHiraChar') {
                    if (!/^[\u3040-\u309F]+$/.test(data)) {
                      errors.push(key + ':' + item.key)
                    }
                  }
                  if (item.key === 'fullKanaChar') {
                    if (!/^[\u30A0-\u30FF]+$/.test(data)) {
                      errors.push(key + ':' + item.key)
                    }
                  }
                  if (item.key === 'fullNumeral') {
                    if (!/^[\uFF10-\uFF19]+$/.test(data)) {
                      errors.push(key + ':' + item.key)
                    }
                  }

                  if (item.key === 'fullAllChar') {
                    //dataから許可されているspecifiedLettersを事前に取り除く
                    const str = specifiedLetters.reduce((acc, cur) => acc.replaceAll(cur, ''), data)
                    //全角文字は、JIS X 0208 を使用する。漢字については、JIS第1水準漢字～JIS第4水準漢字とする。なお、文字水準の範囲については、連携先システム毎に府省側で設定が可能で、設定された文字水準の範囲であるかをチェックする。
                    //全角記号のうち、「―(全角ダッシュ)」「～」「∥」「－(全角マイナス)」「￠」「￡」「￢」の７文字についてはIBMUnicodeではなくMSUnicodeのみを許容する。制御文字は使用不可とする。

                    // 許容される文字の範囲（全角文字、JIS第1水準漢字からJIS第4水準漢字）
                    const regex = /^[\u3000-\u301C\u301A-\u30FF\uFF01-\uFF9F\u3400-\u4DBF\u4E00-\u9FFF\uF900-\uFAFF\u2014\u301C\u2016\u2212\u00A2\u00A3\u00A5]*$/
                    // IBM Unicodeではなく、MS Unicodeのみを許容する7つの全角記号
                    const invalidChars = /[\u2015\u2225\uFF0D\uFFE0-\uFFE1\uFFE5]/
                    // 制御文字の範囲
                    const controlChars = /[\u0000-\u001F\u007F-\u009F]/
                    if (!regex.test(str) || invalidChars.test(str) || controlChars.test(str)) {
                      errors.push(key + ':' + item.key)
                    }
                  }

                  if (item.key === 'nonspace') {
                    //半角及び全角の空白文字使用不可
                    if (data.match(/[\s]/)) {
                      errors.push(key + ':' + item.key)
                    }
                  }

                  if (item.key === 'date') {
                    const list1 = data.map(item => item.tagName)
                    const list2 = item.operators
                      .map(operator => operator.key)
                      .map(key => {
                        switch (key) {
                          case 'era':
                            return '年号'
                          case 'year':
                            return '年'
                          case 'month':
                            return '月'
                          case 'day':
                            return '日'
                          case 'nendo':
                            return '年度'
                          case 'yyyymmdd':
                            return '西暦'
                        }
                      })
                    if (list1.join(',') !== list2.join(',')) {
                      errors.push(key + ':' + item.key)
                    }
                  }
                  if (item.key === 'mail') {
                    //チェック項目とする場合、空タグとして置く。実際に存在するアドレスかどうかをチェックするわけではなく xxx@xxx と@を挟んだ両側の文字列がすべて ASCII コードかをチェックする。
                    if (!data.match(/[^a-zA-Z0-9@.]/)) {
                      errors.push(key + ':' + item.key)
                    }
                  }
                  if (item.key === 'resident') {
                    //11 桁の半角数字かどうかチェックする。
                    if (!data.match(/[^0-9]/) || data.length !== 11) {
                      errors.push(key + ':' + item.key)
                    }
                  }
                  if (key === 'post') {
                    //郵便番号 xxx-yyyyとハイフンを挟んだ3桁-4桁の8字半角数字
                    if (!data.match(/^[0-9]{3}-[0-9]{4}$/)) {
                      errors.push(key + ':' + item.key)
                    }
                  }
                  if (key === 'tel') {
                    //xxxx-yyyy-zzzz と２つのハイフンを挟んだ半角数字が存在するかをチェックする。（桁数は考慮に入れない）
                    if (!data.match(/[^0-9-]/) || data.split('-').length !== 3) {
                      errors.push(key + ':' + item.key)
                    }
                  }
                })
              }

              if (key === 'char' || key === 'numerical') {
                items.forEach(item => {
                  if (item.key === 'contents') {
                    if (
                      !item.operators.some(operator => {
                        if (operator.key === 'equal') {
                          return data === operator.value[0]
                        }
                        if (operator.key === 'notEqual') {
                          return data !== operator.value[0]
                        }
                      })
                    )
                      errors.push(key + ':' + item.key)
                  }
                  if (item.key === 'range') {
                    if (
                      !item.operators.some(operator => {
                        if (operator.key === 'equal') {
                          return data.length === parseFloat(operator.value[0])
                        }
                        if (operator.key === 'within') {
                          return data.length <= parseFloat(operator.value[0])
                        }
                      })
                    )
                      errors.push(key + ':' + item.key)
                  }
                  if (item.key === 'intDigit') {
                    if (
                      !item.operators.some(operator => {
                        let [intDigit, decimalDigit] = data.split('.')
                        if (operator.key === 'equal') {
                          return intDigit.length === parseFloat(operator.value[0])
                        }
                        if (operator.key === 'within') {
                          return intDigit.length <= parseFloat(operator.value[0])
                        }
                      })
                    )
                      errors.push(key + ':' + item.key)
                  }
                  if (item.key === 'decimalDigit') {
                    if (
                      !item.operators.some(operator => {
                        let [intDigit, decimalDigit] = data.split('.')
                        if (decimalDigit === undefined) decimalDigit = ''
                        if (operator.key === 'equal') {
                          return decimalDigit.length === parseFloat(operator.value[0])
                        }
                        if (operator.key === 'within') {
                          return decimalDigit.length <= parseFloat(operator.value[0])
                        }
                      })
                    )
                      errors.push(key + ':' + item.key)
                  }
                  if (item.key === 'point') {
                    if (
                      !item.operators.some(operator => {
                        if (operator.key === 'equal') {
                          return parseFloat(data) === parseFloat(operator.value[0])
                        }
                        if (operator.key === 'moreThan') {
                          return parseFloat(data) > parseFloat(operator.value[0])
                        }
                        if (operator.key === 'lessThan') {
                          return parseFloat(data) < parseFloat(operator.value[0])
                        }
                      })
                    )
                      errors.push(key + ':' + item.key)
                  }
                })
              }
              list.push({ key, errors })
            }
          }

          //相関単項目チェックがある場合は、相関項目チェックを行い、相関が成立した場合のみ単項目チェック
          if (correlationCheckItem) {
            let _check = []
            correlation.forEach(item => {
              const elm = document.querySelector(`[name="${item.errtag[0]}"]`)
              if (elm) {
                checkInput(item.inputCheck[0], elm.value, _check)
              }
            })
            _check = _check.map(c => c.errors.length === 0)
            if (logic === 'and') {
              //全ての条件が成立していればOK
              if (_check.every(item => item)) {
                checkInput(inputCheck, data, list)
              }
            } else if (logic === 'or') {
              //どれか一つでも成立していればOK
              if (_check.some(item => item)) {
                checkInput(inputCheck, data, list)
              }
            } else if (logic === 'xor') {
              //どれか一つだけ成立していればOK
              if (_check.filter(item => item).length === 1) {
                checkInput(inputCheck, data, list)
              }
            } else if (logic === 'nand') {
              //どれか一つでも成立していなければOK
              if (_check.some(item => !item)) {
                checkInput(inputCheck, data, list)
              }
            } else if (logic === 'nor') {
              //全ての条件が成立していなければOK
              if (_check.every(item => !item)) {
                checkInput(inputCheck, data, list)
              }
            }
            check.push(list)
          } else {
            //相関単項目チェックがない場合は、単項目チェックのみ
            checkInput(inputCheck, data, list)
            check.push(list)
          }
        })
      let errors = check
        .map(item => item.map(item => item.errors))
        .flat()
        .flat()
      return {
        check,
        errors: errors,
        nl,
      }
    }

    let xsl = XSLFile

    if (!egovStore.isPreview) {
      //xslからコメントを削除　<!--から-->
      xsl = xsl.replace(/<!--[\s\S]*?-->/g, '')
      let choose = []
      choose = xsl.match(/<xsl:if\b[^>]*>(?:(?!<\/xsl:if>).)*<\/xsl:if>(\s*<xsl:if\b[^>]*>(?:(?!<\/xsl:if>).)*<\/xsl:if>)+/g)

      if (choose) {
        for (const choice of choose) {
          // testAttributeを正規表現で抽出
          const testMatch = choice.match(/test="([^"]*)"/)
          if (testMatch && testMatch[1]) {
            const testAttribute = testMatch[1]
            const tag = testAttribute
              .replace(/(translate|format-number|substring|normalize-space|substring-before)\(/g, '')
              .replaceAll('[1]', '')
              .replaceAll('/text()', '')
              .split('[')[0]

            let value = tag.split('/').reduce((acc, key) => acc && (Array.isArray(acc) ? acc[0][key] : acc[key]), DataTarget)
            if (Array.isArray(value)) {
              value = value[0]
            }

            // xsl:attributeの有無でケース分け
            if (!choice.includes('<xsl:attribute')) {
              // 正規表現で選択肢を直接抽出
              const optionRegex = /<xsl:if[^>]*test="[^"]*\[. = '([^']+)'\][^"]*">([^<]+)<\/xsl:if>/g
              const options = []
              let match

              while ((match = optionRegex.exec(choice)) !== null) {
                if (match[1] && match[2]) {
                  options.push([match[1], match[2]])
                }
              }

              if (options.length > 0) {
                xsl = xsl.replace(choice, `<select class="full" name="${tag}"><option></option>${options.map(item => `<option value="${item[0]}" ${item[0] === value ? 'selected=""' : ''}>${item[1]}</option>`).join('')}</select>`)
              }
            } else {
              // not()関数を含む場合はスキップ
              if (testAttribute.startsWith('not(')) {
                continue
              }

              const name = testAttribute.replaceAll('[1]', '').replaceAll('/text()', '').replaceAll(' ', '').split("[.='")[0]

              xsl = xsl.replace(choice, choice + `<xsl:attribute name="name">${name}</xsl:attribute>`)
              xsl = xsl.replace(choice, choice + `<xsl:attribute name="onclick"></xsl:attribute>`)
            }
          } else {
          }
        }
      }

      choose = xsl.match(/<xsl:choose>[\s\S]*?<\/xsl:choose>/g)
      if (choose) {
        for (const choice of choose) {
          const tag = choice
            .match(/select="([^"]*)"/)[1]
            .replace(/(translate|format-number|substring|normalize-space|substring-before)\(/g, '')
            .split(',')[0]
            .replaceAll('[1]', '')
            .replaceAll('/text()', '')
          let value = tag.split('/').reduce((acc, key) => acc && (Array.isArray(acc) ? acc[0][key] : acc[key]), DataTarget)
          if (Array.isArray(value)) {
            value = value[0]
          }
          if (['帳票名'].includes(tag) || tag === '' || value === undefined) {
            continue
          }

          //入力ルール
          const inputDatas = []
          CheckObject.checkRoot.checkItem
            .filter(item => item.errtag[0] === tag)
            .map(element => {
              element.inputCheck.forEach(check => {
                if (check.inputData) {
                  inputDatas.push(...Object.keys(check.inputData[0]))
                }
              })
            })

          //バリデーションチェック
          const check = validation(tag, value)
          const hasError = check.errors.length > 0
          const classList = []
          if (hasError) {
            classList.push('error')
          }

          if (tag.startsWith('届出年月日')) {
            classList.push('full')
          }

          //改行がある場合
          if (check.nl) {
            xsl = xsl.replace(choice, `<textarea class="${classList.join(' ')}" name="${tag}" data-rule="${inputDatas.join(',')}">${value}</textarea>`)
          } else {
            xsl = xsl.replace(choice, `<input class="${classList.join(' ')}" type="text" name="${tag}" value="${value}" data-rule="${inputDatas.join(',')}"></input>`)
          }
        }
      }
    }

    //加工後のオブジェクトをXMLに変換
    const xmlDoc = new DOMParser().parseFromString(builder.buildObject(DataObject), 'text/xml')
    const xsltDoc = new DOMParser().parseFromString(xsl, 'text/xml')
    const processor = new XSLTProcessor()
    processor.importStylesheet(xsltDoc)
    const resultFragment = processor.transformToFragment(xmlDoc, document)

    //なんか出来上がる書類の右と下の余白が無いから、それを調整する
    const DOM = new DOMParser().parseFromString(new XMLSerializer().serializeToString(resultFragment), 'text/html')
    const form = DOM.querySelector('form')
    const lefts = []
    const tops = []
    const widths = []
    const heights = []
    form.querySelectorAll('span').forEach(element => {
      const left = element.style.left
      if (left !== '0px' && left) {
        const _left = parseInt(left)
        lefts.push(_left)
        const width = parseInt(element.style.width)
        widths.push(width ? width + _left : element.offsetWidth + _left)
      }
      const top = element.style.top
      if (top !== '0px' && top) {
        const _top = parseInt(top)
        tops.push(_top)
        const height = parseInt(element.style.height)
        heights.push(height ? height + _top : element.offsetHeight + _top)
      }
    })

    const div = form.querySelector('div')
    const divWidth = parseInt(div.style.width)
    const divHeight = parseInt(div.style.height)

    const leftMargin = Math.min(...lefts)
    const topMargin = Math.min(...tops)
    const rightMargin = divWidth - Math.max(...widths)
    const bottomMargin = divHeight - Math.max(...heights)

    div.style.width = `${divWidth + (leftMargin - rightMargin)}px`
    div.style.height = `${divHeight + (topMargin - bottomMargin)}px`

    if (!egovStore.isPreview) {
      //disabledでreadonlyがないinputタグを抽出
      form.querySelectorAll('input').forEach(element => {
        if (element.disabled && !element.readOnly) {
          //disabled属性を削除
          element.removeAttribute('disabled')
        }
      })
    }
    document.querySelector('.paper').innerHTML = form.outerHTML

    function toFullWidth(str) {
      // 半角英数字を全角に変換
      str = str.replace(/[A-Za-z0-9]/g, function (s) {
        return String.fromCharCode(s.charCodeAt(0) + 0xfee0)
      })
      return str
    }

    function toHalfWidth(str) {
      // 全角英数字を半角に変換
      str = str.replace(/[Ａ-Ｚａ-ｚ０-９]/g, function (s) {
        return String.fromCharCode(s.charCodeAt(0) - 0xfee0)
      })
      return str
    }

    function changeValue(e) {
      if (e.target.dataset.rule) {
        const roles = e.target.dataset.rule.split(',')
        roles.forEach(role => {
          switch (role) {
            case 'fullNumeral':
              e.target.value = toFullWidth(e.target.value)
              break
            case 'halfEnglish':
              e.target.value = toHalfWidth(e.target.value)
              break
          }
        })
      }

      const name = e.target.name
      let value = e.target.value

      // ラジオボタンとチェックボックスの処理
      if (e.target.type === 'radio') {
        // ラジオボタンがチェックされていない場合は何もしない
        if (!e.target.checked) {
          return
        }
        // チェックされている場合はそのままvalue値を使用
      } else if (e.target.type === 'checkbox' && !e.target.checked) {
        value = '' // チェックボックスは未チェック時に空文字に
      }

      // path変数の宣言を先に移動
      const path = name.split('/')
      if (!DataTarget[path[0]]) return

      // 現在の値の取得とデータ型を判断
      const getCurrentValue = (obj, pathArr) => {
        let current = obj
        for (let i = 0; i < pathArr.length; i++) {
          const key = pathArr[i]
          if (i === 0) {
            current = current[key][0]
          } else {
            if (!current[key]) return undefined
            current = Array.isArray(current[key]) ? current[key][0] : current[key]
          }
        }
        return current
      }

      // 元の値を取得
      const originalValue = getCurrentValue(DataTarget, path)

      // 値が配列に入っているかチェック
      const shouldBeArray = Array.isArray(originalValue) || (originalValue === undefined && e.target.type === 'radio')

      // 値を設定
      const setValue = shouldBeArray ? [value] : value

      if (path.length === 1) {
        DataTarget[path[0]][0] = setValue
      } else if (path.length === 2) {
        DataTarget[path[0]][0][path[1]] = setValue
      } else if (path.length === 3) {
        DataTarget[path[0]][0][path[1]][0][path[2]] = setValue
      } else if (path.length === 4) {
        DataTarget[path[0]][0][path[1]][0][path[2]][0][path[3]] = setValue
      } else if (path.length === 5) {
        DataTarget[path[0]][0][path[1]][0][path[2]][0][path[3]][0][path[4]] = setValue
      } else if (path.length === 6) {
        DataTarget[path[0]][0][path[1]][0][path[2]][0][path[3]][0][path[4]][0][path[5]] = setValue
      } else if (path.length === 7) {
        DataTarget[path[0]][0][path[1]][0][path[2]][0][path[3]][0][path[4]][0][path[5]][0][path[6]] = setValue
      } else if (path.length === 8) {
        DataTarget[path[0]][0][path[1]][0][path[2]][0][path[3]][0][path[4]][0][path[5]][0][path[6]][0][path[7]] = setValue
      } else if (path.length === 9) {
        DataTarget[path[0]][0][path[1]][0][path[2]][0][path[3]][0][path[4]][0][path[5]][0][path[6]][0][path[7]][0][path[8]] = setValue
      } else if (path.length === 10) {
        DataTarget[path[0]][0][path[1]][0][path[2]][0][path[3]][0][path[4]][0][path[5]][0][path[6]][0][path[7]][0][path[8]][0][path[9]] = setValue
      }

      const check = validation(name, value)
      if (check.errors.length > 0) {
        e.target.classList.add('error')
      } else {
        e.target.classList.remove('error')
      }

      // 現在のフォームの編集フラグを立てる
      egovStore.applyFlagList[egovStore.currentFormID] = true

      // チェックフラグを折る
      egovStore.isChecked = false
    }

    nextTick(() => {
      const elements = document.querySelectorAll('[name]')
      const checkAll = () => {
        elements.forEach(element => {
          changeValue({ target: element })
        })
      }
      elements.forEach(element => {
        element.addEventListener('change', checkAll)
        element.addEventListener('input', checkAll)
      })

      // 初期表示時に一度すべての要素の値を正しく設定
      checkAll()
    })

    const XMLFile = await egovStore.getText(`${egovStore.activeCode}/${path}/${CurrentFormInfo.XMLFile[0]}`)
    await setTabIndex(createTabOrder(XMLFile))

    loading.value.hide()
  }

  // タブオーダー設定
  const createTabOrder = xmlString => {
    let list = []
    function parseXML(xmlString) {
      const parser = new DOMParser()
      const xmlDoc = parser.parseFromString(xmlString, 'text/xml')
      return xmlDoc.documentElement
    }
    function printHierarchy(node, path = '') {
      if (path) {
        list.push(path)
      }
      for (let child of node.children) {
        const childPath = path ? `${path}/${child.tagName}` : child.tagName
        printHierarchy(child, childPath)
      }
    }
    printHierarchy(parseXML(xmlString))
    list = list.filter(item => item.split('/').length !== 1)
    list = list.map(item => item.split('/').slice(1).join('/'))
    return list
  }

  const setTabIndex = async tabOrder => {
    for (const element of document.querySelectorAll('[tabindex]')) {
      element.tabIndex = 0
    }
    tabOrder.forEach((item, index) => {
      const element = document.querySelector(`[name="${item}"]`)
      if (element) {
        element.tabIndex = index + 1
      }
    })
  }

  // 申請データをZIPにしてダウンロード
  const downloadApplyData = async () => {
    const zip = new JSZip()
    const folder = zip.folder(egovStore.currentProcInfo.ProcID[0])

    // Add kouseiXml
    const kouseiXml = egovStore.buildXml(egovStore.kouseiObject['DataRoot']).replace('<DataRoot>', `<?xml-stylesheet href="${egovStore.kouseiObject['DataRoot']['STYLESHEET'][0]}" type="text/xsl"?>\n<DataRoot>`)
    folder.file('kousei.xml', kouseiXml)

    // Add applyDataList XML files
    egovStore.applyDataList.forEach(item => {
      folder.file(item.Filename, item.DataXml)
    })

    // Add attached files
    egovStore.attachedFiles.forEach(file => {
      folder.file(file.Filename, file.Data)
    })

    // Generate zip file and trigger download
    const content = await zip.generateAsync({ type: 'blob' })
    // const fileName = `${egovStore.applicantCompanyName}_${egovStore.getDateTimeString()}_${egovStore.adminProcName}.zip`
    saveAs(content, egovStore.generateZipFileName())
  }
</script>

<template>
  <Header />
  <main class="app-main">
    <div class="main-header">
      <h2>e-Gov申請書類作成</h2>
      <span class="proc-name">{{ egovStore.adminProcName }}</span>
      <input type="file" hidden @change="selectSaveData" ref="fileInput" accept="application/json" />
      <div class="import-export">
        <img class="load" src="@/assets/img/load.svg" alt="" @click="fileInput.click" />
        <img class="save" src="@/assets/img/save.svg" alt="" @click="downloadSaveData" />
      </div>
    </div>
    <div class="main-body">
      <div class="create-egov">
        <div class="grid" :class="{ close: !showKousei }">
          <div class="edit">
            <div class="select large">
              <select v-model="egovStore.currentFormID" @change="createForm($event.target.value)">
                <option v-for="option in egovStore.formOptions" :key="option.id" :value="option.value">
                  {{ option.name }}
                </option>
              </select>
            </div>
            <div class="input-list scroller">
              <template v-if="egovStore.kouseiObject['DataRoot']">
                <details>
                  <summary>申請者情報入力 (自動入力)</summary>
                  <dl>
                    <dt>氏名</dt>
                    <dd>
                      <div class="name-grid">
                        <input id="applicant-last-name" type="text" v-model="egovStore.applicantInfo.lastName" v-autokana:applicant-last-name @input="handleApplicantFullWidthInput($event, 'lastName')" placeholder="姓" />
                        <input id="applicant-first-name" type="text" v-model="egovStore.applicantInfo.firstName" v-autokana:applicant-first-name @input="handleApplicantFullWidthInput($event, 'firstName')" placeholder="名" />
                      </div>
                    </dd>
                    <dt>氏名カナ</dt>
                    <dd>
                      <div class="name-grid">
                        <input id="applicant-last-name-kana" type="text" v-model="egovStore.applicantInfo.lastKana" @input="handleApplicantFullWidthInput($event, 'lastKana')" placeholder="セイ" />
                        <input id="applicant-first-name-kana" type="text" v-model="egovStore.applicantInfo.firstKana" @input="handleApplicantFullWidthInput($event, 'firstKana')" placeholder="メイ" />
                      </div>
                    </dd>
                    <dt>役職</dt>
                    <dd><input type="text" v-model="egovStore.applicantInfo.title" @input="handleApplicantFullWidthInput($event, 'title')" placeholder="全角で入力してください" /></dd>
                    <dt>法人団体名</dt>
                    <dd><input id="applicant-company-name" type="text" v-model="egovStore.applicantInfo.companyName" v-autokana:applicant-company-name @input="handleApplicantFullWidthInput($event, 'companyName')" placeholder="全角で入力してください" /></dd>
                    <dt>法人団体名カナ</dt>
                    <dd><input id="applicant-company-name-kana" type="text" v-model="egovStore.applicantInfo.companyNameKana" @input="handleApplicantFullWidthInput($event, 'companyNameKana')" placeholder="全角カタカナで入力してください" /></dd>
                    <dt>部門名</dt>
                    <dd><input id="applicant-division" type="text" v-model="egovStore.applicantInfo.division" v-autokana:applicant-division @input="handleApplicantFullWidthInput($event, 'division')" placeholder="全角で入力してください" /></dd>
                    <dt>部門名カナ</dt>
                    <dd><input id="applicant-division-kana" type="text" v-model="egovStore.applicantInfo.divisionKana" @input="handleApplicantFullWidthInput($event, 'divisionKana')" placeholder="全角カタカナで入力してください" /></dd>
                    <dt>郵便番号</dt>
                    <dd>
                      <div class="postal-code-grid">
                        <input type="text" v-model="egovStore.applicantInfo.postalCode[0]" placeholder="123" maxlength="3" @input="handleApplicantPostalCodeInput($event, 0)" />
                        <span class="hyphen">-</span>
                        <input type="text" v-model="egovStore.applicantInfo.postalCode[1]" placeholder="4567" maxlength="4" @input="handleApplicantPostalCodeInput($event, 1)" />
                      </div>
                    </dd>
                    <dt>住所</dt>
                    <dd><input id="applicant-address" type="text" v-model="egovStore.applicantInfo.address" v-autokana:applicant-address @input="handleApplicantFullWidthInput($event, 'address')" placeholder="全角で入力してください" /></dd>
                    <dt>住所カナ</dt>
                    <dd><input id="applicant-address-kana" type="text" v-model="egovStore.applicantInfo.addressKana" @input="handleApplicantFullWidthInput($event, 'addressKana')" placeholder="全角カタカナで入力してください" /></dd>
                    <dt>電話番号</dt>
                    <dd>
                      <div class="phone-grid">
                        <input type="text" v-model="egovStore.applicantInfo.phone[0]" placeholder="012" maxlength="5" @input="handleApplicantPhoneInput($event, 0)" />
                        <span class="hyphen">-</span>
                        <input type="text" v-model="egovStore.applicantInfo.phone[1]" placeholder="3456" maxlength="4" @input="handleApplicantPhoneInput($event, 1)" />
                        <span class="hyphen">-</span>
                        <input type="text" v-model="egovStore.applicantInfo.phone[2]" placeholder="7890" maxlength="4" @input="handleApplicantPhoneInput($event, 2)" />
                      </div>
                    </dd>
                    <dt>FAX番号</dt>
                    <dd>
                      <div class="phone-grid">
                        <input type="text" v-model="egovStore.applicantInfo.fax[0]" placeholder="012" maxlength="5" @input="handleApplicantFaxInput($event, 0)" />
                        <span class="hyphen">-</span>
                        <input type="text" v-model="egovStore.applicantInfo.fax[1]" placeholder="3456" maxlength="4" @input="handleApplicantFaxInput($event, 1)" />
                        <span class="hyphen">-</span>
                        <input type="text" v-model="egovStore.applicantInfo.fax[2]" placeholder="7890" maxlength="4" @input="handleApplicantFaxInput($event, 2)" />
                      </div>
                    </dd>
                    <dt>メールアドレス</dt>
                    <dd><input type="email" v-model="egovStore.applicantInfo.email" @input="handleApplicantEmailInput" /></dd>
                  </dl>
                </details>
                <details>
                  <summary>連絡先情報入力 (自動入力)</summary>
                  <dl>
                    <dt>氏名</dt>
                    <dd>
                      <div class="name-grid">
                        <input id="contact-last-name" type="text" v-model="egovStore.contactInfo.lastName" v-autokana:contact-last-name @input="handleContactFullWidthInput($event, 'lastName')" placeholder="姓" />
                        <input id="contact-first-name" type="text" v-model="egovStore.contactInfo.firstName" v-autokana:contact-first-name @input="handleContactFullWidthInput($event, 'firstName')" placeholder="名" />
                      </div>
                    </dd>
                    <dt>氏名カナ</dt>
                    <dd>
                      <div class="name-grid">
                        <input id="contact-last-name-kana" type="text" v-model="egovStore.contactInfo.lastKana" @input="handleContactFullWidthInput($event, 'lastKana')" placeholder="セイ" />
                        <input id="contact-first-name-kana" type="text" v-model="egovStore.contactInfo.firstKana" @input="handleContactFullWidthInput($event, 'firstKana')" placeholder="メイ" />
                      </div>
                    </dd>
                    <dt>役職</dt>
                    <dd><input type="text" v-model="egovStore.contactInfo.title" @input="handleContactFullWidthInput($event, 'title')" placeholder="全角で入力してください" /></dd>
                    <dt>法人団体名</dt>
                    <dd>
                      <input id="contact-company-name" type="text" v-model="egovStore.contactInfo.companyName" v-autokana:contact-company-name @input="handleContactFullWidthInput($event, 'companyName')" placeholder="全角で入力してください" />
                    </dd>
                    <dt>法人団体名カナ</dt>
                    <dd><input id="contact-company-name-kana" type="text" v-model="egovStore.contactInfo.companyNameKana" @input="handleContactFullWidthInput($event, 'companyNameKana')" placeholder="全角カタカナで入力してください" /></dd>
                    <dt>部門名</dt>
                    <dd><input id="contact-division" type="text" v-model="egovStore.contactInfo.division" v-autokana:contact-division @input="handleContactFullWidthInput($event, 'division')" placeholder="全角で入力してください" /></dd>
                    <dt>部門名カナ</dt>
                    <dd><input id="contact-division-kana" type="text" v-model="egovStore.contactInfo.divisionKana" @input="handleContactFullWidthInput($event, 'divisionKana')" placeholder="全角カタカナで入力してください" /></dd>
                    <dt>郵便番号</dt>
                    <dd>
                      <div class="postal-code-grid">
                        <input type="text" v-model="egovStore.contactInfo.postalCode[0]" placeholder="123" maxlength="3" @input="handleContactPostalCodeInput($event, 0)" />
                        <span class="hyphen">-</span>
                        <input type="text" v-model="egovStore.contactInfo.postalCode[1]" placeholder="4567" maxlength="4" @input="handleContactPostalCodeInput($event, 1)" />
                      </div>
                    </dd>
                    <dt>住所</dt>
                    <dd><input id="contact-address" type="text" v-model="egovStore.contactInfo.address" v-autokana:contact-address @input="handleContactFullWidthInput($event, 'address')" placeholder="全角で入力してください" /></dd>
                    <dt>住所カナ</dt>
                    <dd><input id="contact-address-kana" type="text" v-model="egovStore.contactInfo.addressKana" @input="handleContactFullWidthInput($event, 'addressKana')" placeholder="全角カタカナで入力してください" /></dd>
                    <dt>電話番号</dt>
                    <dd>
                      <div class="phone-grid">
                        <input type="text" v-model="egovStore.contactInfo.phone[0]" placeholder="012" maxlength="5" @input="handleContactPhoneInput($event, 0)" />
                        <span class="hyphen">-</span>
                        <input type="text" v-model="egovStore.contactInfo.phone[1]" placeholder="3456" maxlength="4" @input="handleContactPhoneInput($event, 1)" />
                        <span class="hyphen">-</span>
                        <input type="text" v-model="egovStore.contactInfo.phone[2]" placeholder="7890" maxlength="4" @input="handleContactPhoneInput($event, 2)" />
                      </div>
                    </dd>
                    <dt>FAX番号</dt>
                    <dd>
                      <!-- <input type="tel" v-model="egovStore.contactInfo.fax" /> -->
                      <div class="phone-grid">
                        <input type="text" v-model="egovStore.contactInfo.fax[0]" placeholder="012" maxlength="5" @input="handleContactFaxInput($event, 0)" />
                        <span class="hyphen">-</span>
                        <input type="text" v-model="egovStore.contactInfo.fax[1]" placeholder="3456" maxlength="4" @input="handleContactFaxInput($event, 1)" />
                        <span class="hyphen">-</span>
                        <input type="text" v-model="egovStore.contactInfo.fax[2]" placeholder="7890" maxlength="4" @input="handleContactFaxInput($event, 2)" />
                      </div>
                    </dd>
                    <dt>メールアドレス</dt>
                    <dd><input type="email" v-model="egovStore.contactInfo.email" @input="handleContactEmailInput" /></dd>
                  </dl>
                </details>
                <details v-if="egovStore.attachedFiles.length">
                  <summary>添付書類</summary>
                  <div class="attached-files">
                    <div v-for="(item, index) in egovStore.attachedFiles" :key="index">
                      <span class="title">{{ item.Title }}</span>
                      <input v-if="!item.Data || item.Data === ''" type="file" @change="selectFile(item, $event)" accept="*" />
                      <div v-else>
                        <div class="name">
                          {{ item.Filename }}
                        </div>
                        <button @click="clearFile(item)">添付データ削除</button>
                      </div>
                    </div>
                  </div>
                </details>
                <details>
                  <summary>提出先指定</summary>
                  <dl>
                    <dt>大分類</dt>
                    <dd>
                      <div class="select">
                        <select v-model="egovStore.major" @change="egovStore.setSubmissionTarget(egovStore.major, null, null)">
                          <option></option>
                          <option v-for="item in egovStore.majorOptions" :key="item" :value="item">{{ item }}</option>
                        </select>
                      </div>
                    </dd>
                    <dt>中分類</dt>
                    <dd>
                      <div class="select">
                        <select v-model="egovStore.medium" @change="egovStore.setSubmissionTarget(egovStore.major, egovStore.medium, null)">
                          <option></option>
                          <option v-for="item in egovStore.mediumOptions" :key="item" :value="item">{{ item }}</option>
                        </select>
                      </div>
                    </dd>
                    <dt>小分類</dt>
                    <dd>
                      <div class="select">
                        <select v-model="egovStore.minor" @change="egovStore.setSubmissionTarget(egovStore.major, egovStore.medium, egovStore.minor)">
                          <option></option>
                          <option v-for="item in egovStore.minorOptions" :key="item" :value="item">{{ item }}</option>
                        </select>
                      </div>
                    </dd>
                  </dl>
                </details>
              </template>
            </div>
            <div class="action">
              <button type="button" class="request" @click="onApply">{{ egovStore.isChecked ? 'この内容で申請' : '入力チェック' }}</button>
              <button type="button" class="check" @click="changePreview">{{ !egovStore.isPreview ? 'プレビュー/確認' : 'フォーム入力' }}</button>
            </div>
          </div>
          <div class="preview">
            <div class="area">
              <div class="paper" :style="{ transform: `scale(${scale})` }"></div>
            </div>
            <button type="button" class="btn" :class="{ close: showKousei, open: !showKousei }" @click="showKousei = !showKousei"></button>
            <div class="controler">
              <button type="button" @click="zoomOut" :disabled="scale <= scaleList[0]">－</button>
              <button type="button" @click="zoomIn" :disabled="scale >= scaleList[scaleList.length - 1]">＋</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>
  <Footer :breadcrumbs="breadcrumbs" />
  <Loading ref="loading" />
</template>

<style lang="less" scoped>
  .action {
    button {
      padding: 4px 25px;
      height: 30px;

      &.request {
        min-width: 150px;
      }
    }
  }
</style>
